import { get } from "aws-amplify/api";
import Moment from "moment";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from 'react-select';
import { PacmanLoader } from 'react-spinners';
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "react-datepicker/dist/react-datepicker.css";
import "containers/common/List.css";
import "containers/common/Table.css";

import {
    createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const INITIAL_CITYSCOPE = null;
const INITIAL_EXCLUDE_PERPETUAL = true;
const INITIAL_QUERY_TYPE = null;
const INITIAL_PROPERTY_KEY = "";
const INITIAL_ENTERED_DATE = null;
const INITIAL_RESOLUTION_DATE = new Date();

function QueriesQueue({
    isAuthenticated,
    handleInputChange,
}) {
    const queryQueueIdPrefix = "queries-queue";

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
    const [resolutionDate, setResolutionDate] = useState(INITIAL_RESOLUTION_DATE);
    const [enteredDate, setEnteredDate] = useState(INITIAL_ENTERED_DATE);
    const [selectedCityscope, setSelectedCityscope] = useState(null);
    const [cityscopeOptions, setCityscopeOptions] = useState([]);
    const [cityscope, setCityscope] = useState(INITIAL_CITYSCOPE);
    const [excludePerpetual, setExcludePerpetual] = useState(INITIAL_EXCLUDE_PERPETUAL);
    const [selectedQueryType, setSelectedQueryType] = useState(null);
    const [queryTypeOptions, setQueryTypeOptions] = useState([]);
    const [queryType, setQueryType] = useState(INITIAL_QUERY_TYPE);
    const [propertykey, setPropertyKey] = useState(INITIAL_PROPERTY_KEY);

    const filterSelectStyles = useMemo(() => ({
        container: (base) => ({ ...base, width: "100%" }),
        control: (base) => ({ ...base, minHeight: 38, height: 38 }),
        valueContainer: (base) => ({ ...base, height: 38, padding: "0 8px" }),
        indicatorsContainer: (base) => ({ ...base, height: 38 }),
    }), []);

    const createId = useMemo(
        () => createElementIdFactory(queryQueueIdPrefix),
        [queryQueueIdPrefix]
    );

    const tableElementIds = useMemo(
        () => createReusableTableElementIds(createId),
        [createId]
    );

    // --- API Functions ---
    const getData = useCallback(async (params = {}) => {
        try {
            const filteredParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v != null && v !== "")
            );

            const response = await get({
                apiName: "colin",
                path: `/queues/queries`,
                options: {
                    queryParams: filteredParams
                }
            }).response;

            const cs = await response.body.json();
            return cs;
        } catch (error) {
            console.error("API Error", error);
            throw error;
        }
    }, []);

    const getQueryTypes = useCallback(async () => {
        try {
            const response = await get({
                apiName: "colin",
                path: `/querytypes`,
            }).response;

            const cs = await response.body.json();
            return cs;
        } catch (error) {
            console.error("API Error", error);
            throw error;
        }
    }, []);

    const getCityscopes = useCallback(async () => {
        try {
            const response = await get({
                apiName: "colin",
                path: "/cityscopes/dropdown"
            }).response;
            const options = await response.body.json();

            if (options && Array.isArray(options)) {
                const formatted = options.map(opt => ({
                    label: opt.label || opt.cityscope_name || opt,
                    value: opt.value || opt.cityscope_name || opt
                }));
                setCityscopeOptions(options);
                console.log("Fetched Options:", formatted);
            }
        } catch (err) {
            console.error("Error getting cityscopes:", err);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;

        const fetchFilterOptions = async () => {
            setIsLoading(true);
            try {
                await getCityscopes();
                const localqueryTypeOptions = await getQueryTypes();
                setQueryTypeOptions(localqueryTypeOptions);
                
                // Auto-load data on mount
                const localdata = await getData({
                    cityscope: INITIAL_CITYSCOPE
                    , querytype: INITIAL_QUERY_TYPE
                    , excludeperpetual: INITIAL_EXCLUDE_PERPETUAL ? "True" : null
                    , entereddate: INITIAL_ENTERED_DATE !== null ? Moment(INITIAL_ENTERED_DATE).local().format() : null
                    , resolutiondate: INITIAL_RESOLUTION_DATE !== null ? Moment(INITIAL_RESOLUTION_DATE).local().format() : null
                    , propertykey: INITIAL_PROPERTY_KEY
                });
                setData(localdata || []);

            } catch (e) {
                setUi({ showAlert: true, errorMessage: e.message || "Failed to fetch data" });
            } finally {
                setIsLoading(false);
            }
        };

        fetchFilterOptions();
        document.title = "Queries Queue";
    }, [isAuthenticated, getCityscopes, getQueryTypes, getData]);

    // --- Column Definitions ---
    const columns = useMemo(() => [
        columnHelper.accessor("query_id", {
            id: "query_id",
            header: "Query ID",
            cell: (info) => {
                const rowData = info.row.original;
                const queryId = info.getValue();
                if (!rowData || !queryId) return null;
                return (
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/properties/${rowData.property_key}/queries/${queryId}`}
                    >
                        {queryId}
                    </a>
                );
            },
            size: 80,
            meta: { style: { textAlign: "left", verticalAlign: "top" } },
        }),
        columnHelper.accessor("query_type", {
            id: "query_type",
            header: "Query Type",
            size: 100,
            meta: { style: { textAlign: "left", verticalAlign: "top" } },
        }),
        columnHelper.accessor("created_date", {
            id: "created_date",
            header: "Date Created",
            cell: (info) => {
                const val = info.getValue();
                return val ? Moment(val).local().format("DD-MM-YYYY") : null;
            },
            size: 100,
            meta: {
                style: { textAlign: "center" },
            },
        }),
        columnHelper.accessor("date_to_resolve", {
            id: "date_to_resolve",
            header: "Date To Resolve",
            cell: (info) => {
                const val = info.getValue();
                return val ? Moment(val).local().format("DD-MM-YYYY") : null;
            },
            size: 100,
            meta: {
                style: { textAlign: "center" },
            },
        }),
        columnHelper.accessor("perpetual", {
            id: "perpetual",
            header: "Perpetual",
            cell: (info) => {
                const value = info.getValue();

                if (value === null || value === undefined || value === "") {
                    return "";
                }

                if (typeof value === "boolean") {
                    return value ? "true" : "false";
                }

                if (typeof value === "string") {
                    const normalized = value.trim().toLowerCase();
                    if (normalized === "true") return "true";
                    if (normalized === "false") return "false";
                }

                return String(value);
            },
            size: 80,
        }),
        columnHelper.accessor("doc_number", {
            id: "doc_number",
            header: "Doc Number",
            size: 80,
        }),
        columnHelper.accessor("problem", {
            id: "problem",
            header: "Problem",
            cell: (info) => {
                const text = info.getValue();
                return (
                    <div style={{ whiteSpace: "pre-wrap", minHeight: "60px" }}>
                        {text}
                    </div>
                );
            },
            size: 200,
            meta: {
                style: { 'white-space': 'unset' },
            },
        }),
        columnHelper.accessor("property_key", {
            id: "property_key",
            header: "Cityscope Reference",
            cell: (info) => info.getValue(),
            size: 100,
        }),
        columnHelper.accessor("street_address", {
            id: "street_address",
            header: "Street Address",
            cell: (info) => info.getValue(),
            size: 200,
        }),
        columnHelper.accessor("suburb", {
            id: "suburb",
            header: "Suburb",
            size: 100,
        }),
        columnHelper.accessor("title_details", {
            id: "title_details",
            header: "Title",
            size: 200,
            meta: {
                style: { 'white-space': 'unset' },
            },
        }),
        columnHelper.accessor("area", {
            id: "area",
            header: "Area",
            size: 70,
        }),
        columnHelper.accessor("created_by", {
            id: "created_by",
            header: "Created By",
            size: 100,
        }),
    ], []);

    async function handleCityscopeChange(selectedCityscope) {
        setSelectedCityscope(selectedCityscope)
        setCityscope(selectedCityscope.value)
    }

    async function handleQueryTypeChange(selQueryType) {
        if (selQueryType) {
            setSelectedQueryType(selQueryType)
            setQueryType(selQueryType.value)
        } else {
            setQueryType(null);
        }
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setIsLoading(true);

        (async () => {
            try {
                const localdata = await getData({
                    cityscope: cityscope
                    , querytype: queryType
                    , excludeperpetual: excludePerpetual ? "True" : null
                    , entereddate: enteredDate !== null ? Moment(enteredDate).local().format() : null
                    , resolutiondate: resolutionDate !== null ? Moment(resolutionDate).local().format() : null
                    , propertykey: propertykey
                });
                setData(localdata || []);
            } catch (e) {
                alert(e);
            }
            setIsLoading(false);
        })();
    }

    function handleEnteredDateChange(date) {
        if (date) {
            setEnteredDate(new Date(date));
        }
        else {
            setEnteredDate(null);
        }
    }

    function handleResolutionDateChange(date) {
        if (date) {
            setResolutionDate(new Date(date));
        }
        else {
            setResolutionDate(null);
        }
    }

    const handleCheckboxChange = (event) => {
        setExcludePerpetual(event.target.checked);
    };

    if (!isAuthenticated) return null;

    return (
        <div >
            <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
                {ui.errorMessage}
            </Alert>

            <form onSubmit={handleSubmit}>
                <Row className="mt-3 g-2 align-items-end">
                    <Col xs={12} sm={6} lg={2}>
                        <Form.Group controlId="state" >
                            <Form.Label className="text-right" >Cityscope</Form.Label>
                            <Select
                                value={selectedCityscope}
                                onChange={handleCityscopeChange}
                                options={cityscopeOptions}
                                onInputChange={handleInputChange}
                                styles={filterSelectStyles}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} lg={2}>
                        <Form.Group controlId="selectedQueryType" >
                            <Form.Label  >Query Type</Form.Label>
                            <Select
                                value={selectedQueryType}
                                onChange={handleQueryTypeChange}
                                options={queryTypeOptions}
                                onInputChange={handleInputChange}
                                styles={filterSelectStyles}
                            />
                        </Form.Group>
                    </Col >

                    <Col xs={12} sm={6} lg={2}>
                        <Form.Group controlId="enteredDate" >
                            <Form.Label>Queries Entered Since</Form.Label><br />
                            <DatePicker
                                selected={enteredDate}
                                onChange={handleEnteredDateChange}
                                name="enteredDate"
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                style={{ height: "38px" }}
                                isClearable={true}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} lg={2}>
                        <Form.Group controlId="resolutionDate">
                            <Form.Label>Resolution Date Before</Form.Label><br />
                            <DatePicker
                                selected={resolutionDate}
                                onChange={handleResolutionDateChange}
                                name="resolutionDate"
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                style={{ height: "38px" }}
                                isClearable={true}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} lg={2}>
                        <Form.Group controlId="propertykey" >
                            <Form.Label  >Cityscope Ref</Form.Label>
                            <Form.Control
                                onChange={e => setPropertyKey(e.target.value)}
                                value={propertykey}
                                as="input"
                                type="text"
                                placeholder="% Wild card"
                                style={{ height: "38px" }}
                            />
                        </Form.Group>
                    </Col >
                    <Col xs={12} sm={6} lg={1}>
                        <Form.Group controlId="excludePerpetual" >
                            <Form.Label  >Exclude Perpetual</Form.Label>
                            <Form.Check
                                checked={excludePerpetual}
                                onChange={handleCheckboxChange}
                                label=""
                                type="checkbox"
                                name="excludePerpetual"
                                className="m-0"
                                style={{ minHeight: "38px", display: "flex", alignItems: "center" }}
                            />
                        </Form.Group>
                    </Col >
                    <Col xs={12} sm={6} lg={1}>
                        <Button type="submit" variant="primary" className="w-100" style={{ height: "38px" }}>Search</Button>
                    </Col>
                </Row>
            </form>
            <Row>
                <Col><b>{data ? data.length : "0"} of {data ? data.length > 0 ? data[0].result_count : "0" : "0"} records returned.</b></Col>
            </Row>

            <Row>
                <Col>
                    {isLoading ? (
                        <div className="outer-div">
                            <div className="sweet-loading inner-div">
                                <PacmanLoader
                                    cssOverride={{ display: "block", margin: "0 auto" }}
                                    size={20}
                                    color={"#e03c31"}
                                    loading={isLoading}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="custom-data-table">
                            <GenericTable
                                columns={columns}
                                data={data || []}
                                section="queries-queue"
                                elementIds={tableElementIds}
                                pageSize={PAGE_SIZES.queriesQueue}
                                enablePagination={true}
                                hasHeaderSorting={true}
                                enforceMinRowsHeight={true}
                                minVisibleRows={5}
                            />
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
}
export default QueriesQueue;